import { captureAuthOperationGuard } from '@/utils/auth-operation-guard';
/**
 * Chain identity (users SoT):
 * - EOA / WALLET login → user_auth.open_id, projected to users.address (type=1)
 * - EMAIL / GOOGLE → users.address + users.privy_wallet_id (type=2)
 *
 * trade-address HTTP API is a compatibility projection of users.* — not a separate bind table.
 */

import { addTradeAddress, listTradeAddresses } from '@/api/trade';
import { getWalletAddress } from '@/utils/auth-token';
import { extractPrivyTradeAddressFields } from '@/utils/privy';
import { sessionExpectsCustodialWallet } from '@/utils/session-login-track';
import { getWalletSigningProvider } from '@/utils/wallet-provider';
import { ensureUserSessionProfile } from '@/utils/user-session-profile';
import {
  addressesEqual,
  hasCustodialTradeAddress,
  hasSelfCustodyTradeAddress,
  isPrivyTradeAddressInList,
  isTradeAddressDuplicateError,
  pickCustodialTradeAddressRecord,
  resolveCustodialIdentityStatus,
  isWalletOnlyEoaTradeAddresses,
} from '@/utils/trade-address-core';

export {
  addressesEqual,
  getTradeAddressType,
  hasCustodialTradeAddress,
  hasSelfCustodyTradeAddress,
  isWalletOnlyEoaTradeAddresses,
  resolveRedeemExecutionMode,
  pickTradeAddressRecord,
  resolvePrivySignerAddress,
  pickWalletFromTradeAddressList,
} from '@/utils/trade-address-core';

/**
 * Load users chain identity via trade-address/list (backend reads users table).
 */
export async function loadChainIdentityRecords() {
  const raw = await listTradeAddresses();
  const records = Array.isArray(raw) ? raw : [];
  const picked = records[0] ?? null;
  return {
    records,
    address: String(picked?.address ?? '').trim(),
    privyWalletId: String(picked?.privyWalletId ?? '').trim(),
    addressType: Number(picked?.addressType ?? picked?.type ?? 0) || 0,
  };
}

/** Persist users chain identity (Privy type=2 or EOA type=1). */
export async function persistChainIdentity({ address, privyWalletId, type }) {
  const body = { address, type };
  if (privyWalletId) body.privyWalletId = privyWalletId;
  return addTradeAddress(body);
}

/** Custodial users: ensure users.address_type=2 is set from Privy SDK fields. */
export async function ensureCustodialChainIdentity(
  user,
  { assertSession = captureAuthOperationGuard() } = {}
) {
  assertSession();
  const { address, privyWalletId } = extractPrivyTradeAddressFields(user);
  if (!address || !privyWalletId) {
    throw new Error('Privy custodial wallet address or wallet id missing');
  }

  let listed = [];
  try {
    listed = (await loadChainIdentityRecords()).records;
  } catch (error) {
    assertSession();
    console.warn('[chain-identity] list failed before custodial persist', error);
  }
  assertSession();
  const identityStatus = resolveCustodialIdentityStatus(listed, address);
  if (identityStatus === 'matching' || isPrivyTradeAddressInList(listed, address, privyWalletId)) {
    return true;
  }
  if (identityStatus === 'conflict') {
    const registered = pickCustodialTradeAddressRecord(listed);
    const error = new Error('CUSTODIAL_IDENTITY_CONFLICT');
    error.code = 'CUSTODIAL_IDENTITY_CONFLICT';
    error.registeredAddress = String(registered?.address ?? '').trim();
    error.currentAddress = address;
    throw error;
  }

  try {
    assertSession();
    await persistChainIdentity({ address, privyWalletId, type: 2 });
    assertSession();
  } catch (error) {
    assertSession();
    // duplicate 可能是“后台已有另一个托管钱包”，必须重新拉取并核对地址，
    // 不能直接当作绑定成功。
    if (!isTradeAddressDuplicateError(error)) throw error;
  }

  try {
    const { records } = await loadChainIdentityRecords();
    assertSession();
    const verifiedStatus = resolveCustodialIdentityStatus(records, address);
    if (verifiedStatus === 'conflict') {
      const error = new Error('CUSTODIAL_IDENTITY_CONFLICT');
      error.code = 'CUSTODIAL_IDENTITY_CONFLICT';
      throw error;
    }
    if (verifiedStatus !== 'matching') {
      throw new Error('Custodial chain identity persist failed');
    }
  } catch (error) {
    assertSession();
    if (
      error?.code === 'CUSTODIAL_IDENTITY_CONFLICT' ||
      String(error?.message ?? '').includes('Custodial chain identity persist failed')
    ) {
      throw error;
    }
    console.warn('[chain-identity] list verify after custodial persist failed', error);
  }
  return true;
}

/** EOA users: ensure users.address_type=1 from connected wallet (WALLET open_id). */
export async function ensureEoaChainIdentity(walletAddress) {
  const address = String(walletAddress ?? getWalletAddress() ?? '').trim();
  if (!address) return false;
  const { records } = await loadChainIdentityRecords().catch(() => ({ records: [] }));
  const exists = records.some(
    (item) => String(item?.address ?? '').toLowerCase() === address.toLowerCase()
  );
  if (exists) return true;
  try {
    await persistChainIdentity({ address, type: 1 });
    return true;
  } catch (error) {
    if (isTradeAddressDuplicateError(error)) return true;
    console.warn('[chain-identity] EOA persist failed', error);
    return false;
  }
}

/** Redeem capability from users projection (+ connected EOA for wallet session). */
export async function resolveChainIdentityCapability() {
  await ensureUserSessionProfile();
  let records = [];
  let listFailed = false;
  try {
    records = (await loadChainIdentityRecords()).records;
  } catch (error) {
    console.warn('[chain-identity] list failed', error);
    listFailed = true;
    records = [];
  }

  if (!sessionExpectsCustodialWallet()) {
    let connected = String(getWalletAddress() ?? '').trim();
    if (!connected) {
      try {
        const provider = getWalletSigningProvider();
        const accounts = await provider?.request?.({ method: 'eth_accounts' });
        connected = String(accounts?.[0] ?? '').trim();
      } catch {
        connected = '';
      }
    }
    if (connected && !records.some((item) => addressesEqual(item?.address, connected))) {
      records = [...records, { address: connected, addressType: 1 }];
    }
    return {
      records,
      hasCustodial: hasCustodialTradeAddress(records),
      hasSelfCustody: hasSelfCustodyTradeAddress(records) || Boolean(connected),
      isWalletOnlyEoa: isWalletOnlyEoaTradeAddresses(records),
      empty: records.length === 0,
      listFailed,
    };
  }

  return {
    records,
    hasCustodial: hasCustodialTradeAddress(records),
    hasSelfCustody: hasSelfCustodyTradeAddress(records),
    isWalletOnlyEoa: isWalletOnlyEoaTradeAddresses(records),
    empty: records.length === 0,
    listFailed,
  };
}
